/**
 * Typed localStorage helpers.
 * Centralises all storage keys so raw strings never appear in app code.
 */

// All storage keys in one place — no more raw strings
export const STORAGE_KEYS = {
  ACCESS_TOKEN:      'access_token',
  REFRESH_TOKEN:     'refresh_token',
  USER:              'user',
  LAST_ACTIVITY_DATE: 'last_activity_date',  // Tracks last activity ping to avoid spamming
} as const

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

// Typed get — returns null on missing key or JSON parse error
export function getItem<T>(key: string): T | null {
  return getItemFrom<T>(localStorage, key)
}

export function getItemFrom<T>(store: StorageLike, key: string): T | null {
  try {
    const raw = store.getItem(key)
    if (raw === null) return null
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

// Typed set — JSON stringifies automatically
export function setItem<T>(key: string, value: T): void {
  setItemIn(localStorage, key, value)
}

export function setItemIn<T>(store: StorageLike, key: string, value: T): void {
  store.setItem(key, JSON.stringify(value))
}

// Remove one key
export function removeItem(key: string): void {
  removeItemFrom(localStorage, key)
}

export function removeItemFrom(store: StorageLike, key: string): void {
  store.removeItem(key)
}

// Clear all auth keys at once (used in logout)
export function clearAuth(): void {
  clearAuthFrom(localStorage)
}

export function clearAuthFrom(store: StorageLike): void {
  store.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
  store.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
  store.removeItem(STORAGE_KEYS.USER)
  store.removeItem(STORAGE_KEYS.LAST_ACTIVITY_DATE)
}
